import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../hooks/useAuth';
import type { ClaudeModel } from '../utils/modelRecommendation';

interface Workspace {
  id: string;
  account_id: string;
  org_id: string;
  name: string;
  description?: string;
  prompt_template: string;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  trial_ends_at?: string;
  claude_model?: ClaudeModel;
  model_selection_type?: 'auto' | 'manual';
  model_rationale?: string;
  display_order?: number;
  overview_content?: string | null;
  overview_key_insights?: any[] | null;
  overview_generated_at?: string | null;
  overview_status?: string;
  overview_error?: string | null;
  overview_flow_count?: number;
  sf_orgs: {
    id: string;
    name: string;
    instance_url: string;
  };
}

interface WorkspaceContextType {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  currentOrgId: string | null;
  isLoading: boolean;
  error: string | null;
  setCurrentWorkspace: (workspace: Workspace) => void;
  setCurrentOrgId: (orgId: string) => void;
  refreshWorkspaces: () => Promise<void>;
  createWorkspace: (data: {
    org_id: string;
    name: string;
    description?: string;
    prompt_template: string;
    claude_model?: ClaudeModel;
    model_selection_type?: 'auto' | 'manual';
    model_rationale?: string;
  }) => Promise<Workspace>;
  updateWorkspace: (id: string, data: {
    name?: string;
    description?: string;
    prompt_template?: string;
    claude_model?: ClaudeModel;
    model_selection_type?: 'auto' | 'manual';
    model_rationale?: string;
  }) => Promise<Workspace>;
  activateWorkspace: (id: string) => Promise<void>;
  deactivateWorkspace: (id: string) => Promise<void>;
  deleteWorkspace: (id: string) => Promise<void>;
  testPrompt: (workspaceId: string, flowId: string) => Promise<any>;
  updateWorkspaceOrder: (workspaceIds: string[]) => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Save workspace selection to sessionStorage
  const handleSetCurrentWorkspace = (workspace: Workspace | null) => {
    setCurrentWorkspace(workspace);
    if (workspace) {
      sessionStorage.setItem('selectedWorkspaceId', workspace.id);
    } else {
      sessionStorage.removeItem('selectedWorkspaceId');
    }
  };
  
  // Load workspace selection from sessionStorage and auto-select default
  useEffect(() => {
    if (workspaces.length === 0 || !currentOrgId) return;
    
    const orgWorkspaces = workspaces.filter(w => w.org_id === currentOrgId);
    if (orgWorkspaces.length === 0) return;
    
    // First try to restore saved selection
    const savedWorkspaceId = sessionStorage.getItem('selectedWorkspaceId');
    if (savedWorkspaceId) {
      const savedWorkspace = orgWorkspaces.find(w => w.id === savedWorkspaceId);
      if (savedWorkspace) {
        setCurrentWorkspace(savedWorkspace);
        return;
      }
    }
    
    // If no saved selection or saved workspace not found, select default
    if (!currentWorkspace || currentWorkspace.org_id !== currentOrgId) {
      const defaultWorkspace = orgWorkspaces.find(w => w.is_default);
      if (defaultWorkspace) {
        handleSetCurrentWorkspace(defaultWorkspace);
      } else if (orgWorkspaces.length > 0) {
        // Fallback to first workspace if no default
        handleSetCurrentWorkspace(orgWorkspaces[0]);
      }
    }
  }, [workspaces, currentOrgId]);

  // Load workspaces when user changes or org changes
  useEffect(() => {
    if (user && currentOrgId) {
      loadWorkspaces();
    } else {
      setWorkspaces([]);
      setCurrentWorkspace(null);
      setIsLoading(false);
    }
  }, [user, currentOrgId]);

  // Auto-select workspace when workspaces change
  useEffect(() => {
    if (workspaces.length > 0 && !currentWorkspace) {
      // Try to restore from localStorage
      const savedWorkspaceId = localStorage.getItem(`workspace_${currentOrgId}`);
      const savedWorkspace = workspaces.find(w => w.id === savedWorkspaceId);
      
      if (savedWorkspace) {
        setCurrentWorkspace(savedWorkspace);
      } else {
        // Default to the default workspace or first one
        const defaultWorkspace = workspaces.find(w => w.is_default) || workspaces[0];
        setCurrentWorkspace(defaultWorkspace);
      }
    }
  }, [workspaces, currentOrgId]);

  // Save workspace selection
  useEffect(() => {
    if (currentWorkspace && currentOrgId) {
      localStorage.setItem(`workspace_${currentOrgId}`, currentWorkspace.id);
    }
  }, [currentWorkspace, currentOrgId]);

  const loadWorkspaces = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session) {
        throw new Error('No active session');
      }

      const response = await fetch('/api/workspaces', {
        headers: {
          'Authorization': `Bearer ${session.session.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load workspaces');
      }

      const data = await response.json();
      
      // Filter workspaces for current org
      const orgWorkspaces = data.filter((w: Workspace) => w.org_id === currentOrgId);
      setWorkspaces(orgWorkspaces);
      
    } catch (err) {
      console.error('Error loading workspaces:', err);
      setError('Failed to load workspaces');
    } finally {
      setIsLoading(false);
    }
  };

  const createWorkspace = async (data: {
    org_id: string;
    name: string;
    description?: string;
    prompt_template: string;
  }) => {
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session) {
      throw new Error('No active session');
    }

    const response = await fetch('/api/workspaces', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.session.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create workspace');
    }

    const workspace = await response.json();
    await loadWorkspaces(); // Refresh the list
    return workspace;
  };

  const updateWorkspace = async (id: string, data: {
    name?: string;
    description?: string;
    prompt_template?: string;
  }) => {
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session) {
      throw new Error('No active session');
    }

    const response = await fetch(`/api/workspaces/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${session.session.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update workspace');
    }

    const workspace = await response.json();
    await loadWorkspaces(); // Refresh the list
    return workspace;
  };

  const activateWorkspace = async (id: string) => {
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session) {
      throw new Error('No active session');
    }

    const response = await fetch(`/api/workspaces/${id}/activate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.session.access_token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to activate workspace');
    }

    await loadWorkspaces(); // Refresh the list
  };

  const deactivateWorkspace = async (id: string) => {
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session) {
      throw new Error('No active session');
    }

    const response = await fetch(`/api/workspaces/${id}/deactivate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.session.access_token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to deactivate workspace');
    }

    await loadWorkspaces(); // Refresh the list
  };

  const deleteWorkspace = async (id: string) => {
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session) {
      throw new Error('No active session');
    }

    const response = await fetch(`/api/workspaces/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${session.session.access_token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete workspace');
    }

    await loadWorkspaces(); // Refresh the list
  };

  const testPrompt = async (workspaceId: string, flowId: string) => {
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session) {
      throw new Error('No active session');
    }

    const response = await fetch(`/api/workspaces/${workspaceId}/test-prompt`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.session.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ flow_id: flowId })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to test prompt');
    }

    return response.json();
  };

  const updateWorkspaceOrder = async (workspaceIds: string[]) => {
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session) {
      throw new Error('No active session');
    }

    const response = await fetch('/api/workspaces/update-order', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.session.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ workspace_ids: workspaceIds })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update workspace order');
    }

    await loadWorkspaces(); // Refresh the list
  };

  const value: WorkspaceContextType = {
    workspaces,
    currentWorkspace,
    currentOrgId,
    isLoading,
    error,
    setCurrentWorkspace: handleSetCurrentWorkspace,
    setCurrentOrgId,
    refreshWorkspaces: loadWorkspaces,
    createWorkspace,
    updateWorkspace,
    activateWorkspace,
    deactivateWorkspace,
    deleteWorkspace,
    testPrompt,
    updateWorkspaceOrder
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}